import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { useColors } from '../../../../../theme/ColorModeContext';
import { StageSection, Prose, FactGrid, KeyPoints, Callout, CodeSample, MiniLabel, Panel, SegmentedToggle } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'What Is Canton?',
    tagline: 'A network of ledgers, not one public blockchain.',
};

const Hero = () => {
    const colors = useColors();
    return (
        <StageSection gradientBorder eyebrow="Stage 1 of 8">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', mb: 2 }}>
                {['Network of ledgers', 'Privacy by design', 'Built for regulated finance'].map((label) => (
                    <Chip
                        key={label}
                        label={label}
                        size="small"
                        variant="outlined"
                        sx={{ color: colors.accent.cyan, borderColor: colors.border.accent, backgroundColor: colors.accent.cyanGlow, fontFamily: '"Orbitron", sans-serif' }}
                    />
                ))}
            </Box>
            <Typography variant="h2" sx={{
                fontFamily: '"Orbitron", sans-serif',
                fontSize: { xs: '1.9rem', md: '2.6rem' },
                lineHeight: 1.1,
                fontWeight: 800,
                color: colors.text.primary,
                mb: 2,
            }}>
                Not one global ledger. A network of them.
            </Typography>
            <Prose>
                Everything covered so far on this platform (Ethereum, Bitcoin, Solana) shares one assumption: there
                is a single global ledger, and every validator holds a full copy of it. When Alice pays Bob on
                Ethereum, that transaction is broadcast to thousands of nodes, re-executed by every one of them, and
                stored forever where anyone can read it. That radical transparency is exactly what makes those chains
                trustless: you never have to trust anyone's word, because you can check everything yourself.
            </Prose>
            <Prose>
                Canton, built by Digital Asset, starts from a different requirement entirely. It's a protocol that
                lets many independently operated nodes reach shared, tamper-evident agreement on specific
                transactions, <strong>without</strong> sending those transactions to anyone who isn't part of them.
                Each organization keeps its own ledger containing only its own business. There is still one
                consistent picture of "the ledger", but nobody holds all of it: the Canton documentation calls this a{' '}
                <strong>virtual global ledger</strong>, the union of every participant's private slice, which the
                protocol keeps consistent even though no single node ever sees the whole thing.
            </Prose>
            <Prose>
                Toggle between the two models below and pick a transaction. Watch who actually receives the data.
            </Prose>
            <Box sx={{ mt: 2 }}>
                <BroadcastDemo />
            </Box>
        </StageSection>
    );
};

type LedgerModel = 'public' | 'canton';

const ORGS = ['Bank A', 'Bank B', 'Exchange', 'Custodian', 'Fund', 'Regulator'];

const TRADES = [
    { id: 'bond', short: 'Bond trade', label: 'Bank A sells bonds to Bank B', involved: ['Bank A', 'Bank B'] },
    { id: 'fund', short: 'Fund purchase', label: 'Fund buys on Exchange, Custodian holds', involved: ['Fund', 'Exchange', 'Custodian'] },
    { id: 'report', short: 'Regulatory filing', label: 'Bank B files a report with Regulator', involved: ['Bank B', 'Regulator'] },
];

// Node centres on an ellipse around the middle of a 640x380 canvas.
const NODE_POSITIONS = [[320, 52], [512, 122], [512, 258], [320, 328], [128, 258], [128, 122]];

const BroadcastDemo = () => {
    const colors = useColors();
    const [model, setModel] = useState<LedgerModel>('canton');
    const [tradeId, setTradeId] = useState(TRADES[0].id);
    const trade = TRADES.find((item) => item.id === tradeId) ?? TRADES[0];

    const receives = (org: string) => model === 'public' || trade.involved.includes(org);
    const receivedCount = ORGS.filter(receives).length;
    const centreLabel = model === 'public' ? 'Global chain' : 'Synchronizer';
    const centreSub = model === 'public' ? 'stores everything' : 'orders sealed envelopes';

    return (
        <Panel>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', mb: 2 }}>
                <SegmentedToggle<LedgerModel>
                    ariaLabel="Ledger model"
                    value={model}
                    onChange={setModel}
                    options={[{ value: 'public', label: 'Public blockchain' }, { value: 'canton', label: 'Canton' }]}
                />
                <SegmentedToggle<string>
                    ariaLabel="Transaction"
                    value={tradeId}
                    onChange={setTradeId}
                    options={TRADES.map((item) => ({ value: item.id, label: item.short }))}
                />
            </Box>

            <Typography sx={{ color: colors.text.primary, fontWeight: 700, mb: 1 }}>{trade.label}</Typography>

            <Box
                component="svg"
                viewBox="0 0 640 380"
                role="img"
                aria-label={`${receivedCount} of ${ORGS.length} organizations receive the transaction under the ${model === 'public' ? 'public blockchain' : 'Canton'} model`}
                sx={{ width: '100%', height: 'auto', display: 'block' }}
            >
                {ORGS.map((org, index) => {
                    const [x, y] = NODE_POSITIONS[index];
                    const on = receives(org);
                    return (
                        <line
                            key={`line-${org}`}
                            x1={320} y1={190} x2={x} y2={y}
                            stroke={on ? colors.accent.cyan : colors.border.default}
                            strokeWidth={on ? 3 : 1.5}
                            strokeDasharray={on ? undefined : '6 6'}
                            style={{ transition: 'stroke 0.3s ease' }}
                        />
                    );
                })}

                <rect x={225} y={158} width={190} height={64} rx={12} fill={colors.bg.primary} stroke={colors.accent.cyan} strokeWidth={2} />
                <text x={320} y={186} textAnchor="middle" fontSize={19} fontWeight={800} fill={colors.text.primary}>{centreLabel}</text>
                <text x={320} y={208} textAnchor="middle" fontSize={14} fill={colors.text.secondary}>{centreSub}</text>

                {ORGS.map((org, index) => {
                    const [x, y] = NODE_POSITIONS[index];
                    const on = receives(org);
                    return (
                        <g key={org} style={{ transition: 'opacity 0.3s ease' }} opacity={on ? 1 : 0.45}>
                            <rect x={x - 78} y={y - 26} width={156} height={52} rx={10} fill={colors.bg.primary} />
                            <rect
                                x={x - 78} y={y - 26} width={156} height={52} rx={10}
                                fill={on ? colors.accent.cyanGlow : 'transparent'}
                                stroke={on ? colors.accent.cyan : colors.border.strong}
                                strokeWidth={on ? 2 : 1}
                            />
                            <text x={x} y={y - 1} textAnchor="middle" fontSize={19} fontWeight={700} fill={colors.text.primary}>{org}</text>
                            <text x={x} y={y + 17} textAnchor="middle" fontSize={13} fill={on ? colors.accent.cyan : colors.text.muted}>
                                {on ? (trade.involved.includes(org) ? 'party to it' : 'full copy') : 'receives nothing'}
                            </text>
                        </g>
                    );
                })}
            </Box>

            <Typography sx={{ color: colors.text.secondary, lineHeight: 1.65, fontSize: '0.92rem', mt: 1.5 }} aria-live="polite">
                <strong style={{ color: colors.text.primary }}>{receivedCount} of {ORGS.length}</strong> organizations received the
                transaction.{' '}
                {model === 'public'
                    ? 'On a public chain every node gets a full copy, including competitors who had nothing to do with it, and it stays readable forever.'
                    : 'Only the organizations that are party to it receive its contents. The synchronizer in the middle helps order it, but only ever handles encrypted envelopes.'}
            </Typography>
        </Panel>
    );
};

const StackTier = ({ title, body, accent }: { title: string; body: string; accent?: boolean }) => {
    const colors = useColors();
    return (
        <Box sx={{
            p: '12px 14px',
            borderRadius: '8px',
            border: `1px solid ${accent ? colors.border.accent : colors.border.default}`,
            backgroundColor: accent ? colors.accent.cyanGlow : colors.bg.primary,
            textAlign: 'center',
        }}>
            <Typography sx={{ color: colors.text.primary, fontWeight: 800, fontSize: '0.92rem' }}>{title}</Typography>
            <Typography sx={{ color: colors.text.secondary, fontSize: '0.82rem', lineHeight: 1.5 }}>{body}</Typography>
        </Box>
    );
};

const Link = ({ label }: { label: string }) => {
    const colors = useColors();
    return (
        <Typography sx={{ textAlign: 'center', color: colors.text.muted, fontSize: '0.74rem', fontFamily: '"Share Tech Mono", monospace', py: 0.5 }}>
            ↕ {label}
        </Typography>
    );
};

const CantonStack = () => (
    <Panel>
        <MiniLabel>How the pieces stack up</MiniLabel>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: { xs: '10px', md: '24px' } }}>
            {['Bank A', 'Bank B'].map((org) => (
                <Box key={org}>
                    <StackTier title={`${org} app`} body="UI, back office, bots" />
                    <Link label="Ledger API" />
                    <StackTier title={`${org} participant node`} body="DAML engine + its own private contract store" />
                    <Link label="encrypted messages" />
                </Box>
            ))}
            <Box sx={{ gridColumn: '1 / -1' }}>
                <StackTier accent title="Synchronizer" body="Sequencer + mediator: orders and coordinates transactions without reading them" />
            </Box>
        </Box>
    </Panel>
);

const DAML_PREVIEW = `
template Iou
  with
    issuer : Party     -- who owes the money
    owner  : Party     -- who is owed
    amount : Decimal
  where
    signatory issuer   -- must agree for this to exist
    observer owner     -- allowed to see it

    choice Transfer : ContractId Iou
      with newOwner : Party
      controller owner -- only the owner may do this
      do create this with owner = newOwner
`;

// This stage expects 3 quiz questions configured in the admin panel, in this order:
//   Q0 - how Canton is structurally different from a single public blockchain
//   Q1 - why regulated finance specifically needs something other than full public visibility
//   Q2 - what DAML is in relation to Canton
export const blocks: StageBlock[] = [
    content('hero', () => <Hero />),

    content('structure', () => (
        <StageSection title="Many ledgers, one consistent truth">
            <Prose>
                The structural difference is worth stating precisely, because the rest of the track builds on it.
                A public blockchain is <strong>replicated</strong>: every node stores the same complete ledger and
                agrees on it by re-executing every transaction. Canton is <strong>partitioned</strong>: each
                organization's node stores only the contracts its own users are part of, and nodes only communicate
                about transactions they share.
            </Prose>
            <FactGrid items={[
                ['Public blockchain', 'One ledger, fully replicated. Every validator sees and re-executes every transaction. Anyone can audit anything.'],
                ['Canton', 'Many private ledgers stitched into one virtual ledger. Each node sees only its own slice. The involved parties validate their shared transactions.'],
            ]} />
            <Callout title="Not a sidechain, not a private chain">
                Canton isn't a single private blockchain run by a consortium, where every member still sees every
                transaction. Even two members of the same Canton network see nothing of each other's business unless
                they transact together.
            </Callout>
        </StageSection>
    )),

    question(0),

    content('why', () => (
        <StageSection title="Built for institutions that can't work in public">
            <Prose>
                Canton's design target is regulated finance: banks, exchanges, asset managers and market
                infrastructure settling trades, moving tokenized assets and coordinating multi-party workflows. For
                them, a public ledger doesn't just feel uncomfortable, it's often not allowed. Think about what
                broadcasting every transaction would reveal:
            </Prose>
            <FactGrid columns={2} items={[
                ['Positions & strategy', 'Every holding and every trade, readable by competitors, who could front-run large orders or copy strategies.'],
                ['Client relationships', 'Who trades with whom, how often and in what size: commercially sensitive, and usually protected by client confidentiality rules.'],
                ['Personal data', 'Data protection law (such as GDPR) sits badly with an immutable public record that anyone can copy.'],
                ['Regulatory secrecy', 'Banking secrecy, market-abuse and reporting rules decide exactly who may see what. "Everyone sees everything" satisfies none of them.'],
            ]} />
            <Prose>
                The obvious alternative, each institution keeping its own private database, has its own well-known
                cost. Two banks on either side of a trade each record it in their own systems, then spend days
                reconciling the two copies, chasing mismatches and failed settlements. There's no shared source of
                truth, so everything has to be checked twice.
            </Prose>
            <Prose>
                Canton exists to get both at once: the <strong>shared, synchronized truth</strong> of a blockchain,
                so counterparties never have to reconcile, and the <strong>confidentiality</strong> of a private
                database, so nobody learns about business they aren't part of.
            </Prose>
        </StageSection>
    )),

    question(1),

    content('daml', () => (
        <StageSection title="DAML: the language these agreements are written in">
            <Prose>
                If Canton is the protocol and the network, <strong>DAML</strong> (Digital Asset Modeling Language)
                is what runs on it, much as Solidity code runs on the EVM. DAML is a functional language, derived
                from Haskell, purpose-built for multi-party agreements. Rather than describing storage slots and
                function calls, a DAML <strong>template</strong> describes an agreement: who is involved, who had to
                consent to it, who can see it, and which actions each party may take next.
            </Prose>
            <CodeSample language="daml" caption="A first look: a simple Iou template" code={DAML_PREVIEW} />
            <Prose>
                Don't worry about the syntax yet. Notice instead how much of the security model sits right in the
                template: <code>signatory</code>, <code>observer</code> and <code>controller</code> are keywords, not
                conventions. The next stages unpack each one. DAML code is compiled into a package (a <code>.dar</code>{' '}
                file) and uploaded to the participant nodes that need it, so every involved party runs exactly the
                same logic when validating a transaction.
            </Prose>
            <CantonStack />
            <KeyPoints items={[
                <><strong>Canton</strong>: the synchronization protocol and node software that keeps everyone's private ledgers consistent.</>,
                <><strong>DAML</strong>: the smart-contract language describing the agreements, their parties and their permitted actions.</>,
                <>This track stays conceptual. Writing and attacking DAML contracts directly is what the Foundation, Advanced and DAML security tracks are for.</>,
            ]} />
        </StageSection>
    )),

    question(2),
];
